import { NavLink, useParams } from "react-router-dom";
import {
  BarChart3,
  Users,
  Table2,
  Calculator,
  FileText,
  Shield,
  DollarSign,
  ClipboardCheck,
  TrendingUp,
  Settings,
  Download,
  Layers,
  FileSearch,
  ShieldCheck,
  ScrollText,
  Activity,
  Receipt,
  SlidersHorizontal,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Deal navigation — ARGUS-style information architecture.
 *
 * Desktop (lg+): a persistent left sidebar with every section visible at
 * once (no group-switching), grouped the way an underwriter thinks:
 *
 *   Dashboard
 *   PROPERTY   — the model inputs (rent roll, opex, reimbursements, leasing)
 *   ANALYSIS   — the model outputs (pro forma + nested audit, scenarios)
 *              — and the knobs (assumptions)
 *   DOCUMENTS  — source files and data trust (upload, verify, source data)
 *   REPORTS    — exports
 *   MANAGE     — activity log, change history, settings, access (admin)
 *
 * The calculation audit is nested under Pro Forma rather than being a
 * top-level destination. Mobile (<lg): a flat horizontally-scrolling bar
 * with group dividers.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof BarChart3;
  end?: boolean;
  adminOnly?: boolean;
  mobileLabel?: string;
  /** Indented child of the previous item (e.g. Audit under Pro Forma). */
  child?: boolean;
  /** Extra route segments that should keep this item highlighted. */
  alsoMatches?: string[];
}

interface NavSection {
  key: string;
  label: string | null; // null = no group header (Dashboard)
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    key: "overview",
    label: null,
    items: [{ to: "", label: "Dashboard", icon: BarChart3, end: true }],
  },
  {
    key: "property",
    label: "Property",
    items: [
      { to: "rent-roll", label: "Rent Roll", icon: Users },
      {
        to: "operating-statement",
        label: "Operating Statement",
        icon: Receipt,
        mobileLabel: "Op. Stmt",
      },
      { to: "expenses", label: "Expenses", icon: DollarSign },
      {
        to: "reimbursements",
        label: "Reimbursements",
        icon: Calculator,
        mobileLabel: "Reimb.",
      },
      {
        to: "market-leasing",
        label: "Market Leasing",
        icon: TrendingUp,
        mobileLabel: "Mkt Leasing",
      },
    ],
  },
  {
    key: "analysis",
    label: "Analysis",
    items: [
      { to: "cash-flow", label: "Pro Forma", icon: Table2 },
      {
        to: "audit",
        label: "Calculation Audit",
        icon: ClipboardCheck,
        mobileLabel: "Audit",
        child: true,
      },
      { to: "scenarios", label: "Scenarios", icon: Layers },
      {
        to: "assumptions",
        label: "Assumptions",
        icon: SlidersHorizontal,
        mobileLabel: "Assump.",
      },
    ],
  },
  {
    key: "documents",
    label: "Documents",
    items: [
      { to: "documents", label: "Documents", icon: FileText, mobileLabel: "Docs" },
      { to: "verify", label: "Verify Data", icon: ShieldCheck, mobileLabel: "Verify" },
      {
        to: "source-data",
        label: "Source Data",
        icon: FileSearch,
        mobileLabel: "Source",
      },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    items: [{ to: "exports", label: "Exports", icon: Download }],
  },
  {
    key: "manage",
    label: "Manage",
    items: [
      { to: "activity", label: "Activity", icon: Activity },
      {
        to: "audit-trail",
        label: "Change History",
        icon: ScrollText,
        mobileLabel: "History",
      },
      { to: "settings", label: "Settings", icon: Settings },
      { to: "admin", label: "Access", icon: Shield, adminOnly: true },
    ],
  },
];

function useVisibleSections(): NavSection[] {
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  return SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => !i.adminOnly || isAdmin),
  })).filter((s) => s.items.length > 0);
}

/** Desktop sidebar — rendered inside the deal page's flex row. */
export function DealSideNav() {
  const { dealId } = useParams();
  const base = `/deals/${dealId}`;
  const sections = useVisibleSections();

  return (
    <aside className="hidden lg:flex flex-col w-52 shrink-0 border-r border-grove-300 bg-white print:hidden overflow-y-auto">
      <nav className="py-4 px-3 space-y-4">
        {sections.map((section) => (
          <div key={section.key}>
            {section.label && (
              <div className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-widest text-bark-300 select-none">
                {section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to === "" ? base : `${base}/${item.to}`}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-all ${
                      item.child ? "ml-5" : ""
                    } ${
                      isActive
                        ? "bg-forest-50 text-forest-700"
                        : "text-bark-500 hover:text-forest-700 hover:bg-grove-50"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/** Mobile nav — flat horizontally-scrolling bar with group dividers. */
export function DealMobileNav() {
  const { dealId } = useParams();
  const base = `/deals/${dealId}`;
  const sections = useVisibleSections();

  return (
    <nav className="lg:hidden border-b border-grove-300 bg-white print:hidden shrink-0">
      <div className="flex items-center gap-0.5 px-2 overflow-x-auto scrollbar-hide">
        {sections.map((section, si) => (
          <div key={section.key} className="flex items-center gap-0.5">
            {si > 0 && <div className="w-px h-5 bg-grove-200 mx-1 shrink-0" />}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to === "" ? base : `${base}/${item.to}`}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-2.5 py-2.5 text-xs font-medium border-b-2 transition-all whitespace-nowrap ${
                    isActive
                      ? "border-forest-600 text-forest-700"
                      : "border-transparent text-bark-400 hover:text-forest-700"
                  }`
                }
              >
                <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
                {item.mobileLabel || item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}
